<template lang="pug">
#AceptarCotizacion
  b-button(
    variant="action"
    @click="openModal"
  )
    | Aceptar Cotización
    b-spinner(v-if="loading" small)
  //- Modal para aceptar la cotización, marcar abono del cliente
  b-modal.hide-footer(v-model="showModal" no-close-on-backdrop, title="Aceptar cotización")
    template(v-if="ovDetail.requiere_abono && abonoMinimo > 0")
      b-form-group(label-for="abono" :label="`Abono del cliente (${$options.filters.formatCurrency(abonoMinimo)} Mínimo)`")
        CurrencyInput(
          v-model="abono" placeholder="Abono del cliente en pesos ($)" :state="$v.abono.$dirty ? !$v.abono.$error : null" id="ov-action-accept-quotation-abono-input"
        )
        b-form-invalid-feedback( v-if="!$v.abono.$dirty || !$v.abono.required")
          | Campo requerido
        b-form-invalid-feedback( v-if="!$v.abono.$dirty || !$v.abono.minValue")
          | No puede ser menor al abono mínimo de la cotización
        b-form-invalid-feedback( v-if="!$v.abono.$dirty || !$v.abono.maxValue")
          | No puede ser mayor al monto total de la cotización ({{ montoTotalConIva | formatCurrency }})
      
      //- Selector de tipo de documento (solo si requiere abono)
      b-form-group(v-if="ovDetail.requiere_abono && abonoMinimo > 0" label-for="tipo-documento")
        SelectorTipoDocumento(
          @datos-actualizados="onDatosDocumentoActualizados"
          :disabled="loading"
        )
    
    div(v-else)
      | Esta cotización no requiere abono, ¿desea continuar?
    template(#modal-footer='{ ok, cancel, hide }')
      .d-flex(style="justify-content: space-between; width: 100%;")
        b-button.d-block(size='sm' variant='secondary' @click='hideModal' :disabled="loading") Cancelar
          b-spinner(v-if="loading" small)
        b-button.d-block(
          size='sm' 
          variant='primary' 
          @click='acceptQuotation' 
          :disabled="loading || (ovDetail.requiere_abono && abonoMinimo > 0 && !datosDocumento.valido)" 
          id="ov-action-accept-quotation"
        ) Aceptar
          b-spinner(v-if="loading" small)

  //- Modal para solicitar excepción de abono
  b-modal.hide-footer(v-model="showExceptionModal" no-close-on-backdrop title="Solicitar Excepción: Ajuste de Abono")
    div
      p Estás solicitando una excepción para la OV {{ dataOv.id }}, por ingreso de abono menor al requerido.
      p Por favor confirma la solicitud.
      b-form-group(label="Comentario")
        b-form-textarea(
          v-model="exceptionComment"
          placeholder="Agrega información importante para aprobar tu solicitud."
          rows="3"
        )
    template(#modal-footer='{ ok, cancel, hide }')
      .d-flex(style="justify-content: space-between; width: 100%;")
        b-button(variant='secondary' @click='showExceptionModal = false') Volver
        b-button(variant='warning' @click='confirmException' :disabled="loading") Confirmar
          b-spinner(v-if="loading" small)
</template>
<script>
import { required, maxValue } from 'vuelidate/lib/validators'
import { TIPO_EXCEPCION } from '@/utils/enums/exceptions'

import { mapActions, mapGetters } from 'vuex'
import SelectorTipoDocumento from '../../SelectorTipoDocumento.vue'
import CurrencyInput from '../../../CurrencyInput.vue'

export default {
  name: 'AceptarCotizacion',
  components: {
    SelectorTipoDocumento,
    CurrencyInput,
  },
  data() {
    return {
      showModal: false,
      showExceptionModal: false,
      exceptionComment: '',
      loading: false,
      abono: null,
      abonoMinimo: 0,
      // Datos para tipo de documento
      datosDocumento: {
        tipo_documento: 'boleta',
        rut_contribuyente: null,
        valido: true,
      },
    }
  },
  validations() {
    // Solo validar abono si se requiere abono y el abono mínimo es mayor a 0
    if (this.ovDetail.requiere_abono && this.abonoMinimo > 0) {
      return {
        abono: {
          required,
          maxValue: maxValue(this.montoTotalConIva),
        },
      }
    }
    // Si no se requiere abono, no hay validaciones
    return {}
  },
  props: {
    dataOv: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('OVStore', ['ovDetail']),
    montoTotalConIva() {
      // Obtener el monto total con IVA de la OV
      // Puede venir de monto_total_final o calcular desde monto_total_neto
      if (this.ovDetail?.resumen_montos?.monto_total_final) {
        return this.ovDetail.resumen_montos.monto_total_final
      }
      if (this.ovDetail?.monto_total_final) {
        return this.ovDetail.monto_total_final
      }
      if (this.ovDetail?.monto_total_neto) {
        return Math.floor(this.ovDetail.monto_total_neto * 1.19)
      }
      return 0
    },
  },
  methods: {
    ...mapActions('OVStore', [
      'getOvDetail',
      'acceptOv',
      'requestAbonoException',
    ]),
    onDatosDocumentoActualizados(datos) {
      this.datosDocumento = datos
    },
    async openModal() {
      this.loading = true
      await this.getOvDetail(this.dataOv?.id)
      this.abonoMinimo = this.ovDetail?.abono_requerido?.replace(/,/g, '') || 0
      this.abonoMinimo = parseInt(this.abonoMinimo, 10)

      // Si no se requiere abono o el abono mínimo es 0, establecer abono en 0
      if (!this.ovDetail.requiere_abono || this.abonoMinimo === 0) {
        this.abono = 0
        this.nro_boleta_abono = null
      } else {
        this.abono = null
        this.nro_boleta_abono = null
      }

      this.showModal = true
      this.loading = false
    },
    hideModal() {
      this.showModal = false
    },
    acceptQuotation() {
      // Validar primero el formulario solo si se requiere abono
      const requiresAbono = this.ovDetail.requiere_abono && this.abonoMinimo > 0

      if (requiresAbono) {
        this.$v.$touch()
      }

      if (!requiresAbono || !this.$v.$error) {
        // Verificar si el abono es menor al mínimo requerido para solicitar excepción
        if (requiresAbono && Number(this.abono) < this.abonoMinimo) {
          this.showExceptionModal = true
          return
        }

        // Validar que si es factura con abono, el RUT esté validado
        if (requiresAbono && !this.datosDocumento.valido) {
          this.$bvToast.toast(
            'Por favor valida el RUT del contribuyente antes de continuar',
            {
              variant: 'warning',
              solid: true,
            }
          )
          return
        }

        this.loading = true

        const payload = {
          id_oportunidad_venta: this.dataOv?.id,
          monto_abonado: requiresAbono ? this.abono : 0,
          tipo_documento: requiresAbono
            ? this.datosDocumento.tipo_documento
            : 'boleta',
        }

        // Agregar RUT contribuyente si es factura y hay abono
        if (
          requiresAbono &&
          this.datosDocumento.tipo_documento === 'factura' &&
          this.datosDocumento.rut_contribuyente
        ) {
          payload.rut_contribuyente = this.datosDocumento.rut_contribuyente
          // El razon_social_contribuyente lo obtiene el backend desde la info del contribuyente validado
          if (this.datosDocumento.contribuyente_info?.razon_social) {
            payload.razon_social_contribuyente =
              this.datosDocumento.contribuyente_info.razon_social
          }
        }

        this.acceptOv(payload)
          .then((resp) => {
            console.log(resp)
            this.$bvToast.toast(
              'Cotización aceptada y abono registrado correctamente',
              {
                variant: 'success',
                solid: true,
              }
            )
            this.$emit('reloadAll')
            this.loading = false
            this.hideModal()
          })
          .catch((error) => {
            this.$bvToast.toast(
              error.response?.data?.error || 'Error al procesar el pago',
              {
                variant: 'danger',
                solid: true,
              }
            )
            this.loading = false
          })
      }
    },
    confirmException() {
      this.loading = true
      this.requestAbonoException({
        id_oportunidad_venta: this.dataOv?.id,
        comentario: this.exceptionComment,
        monto_minimo_solicitado: this.abono,
        nro_boleta_abono: this.nro_boleta_abono,
        tipo: TIPO_EXCEPCION.ABONO_MENOR,
      })
        .then(() => {
          this.$bvToast.toast('Solicitud de excepción enviada correctamente', {
            title: 'Éxito',
            variant: 'success',
            solid: true,
          })
          this.$emit('reloadAll')
          this.showExceptionModal = false
          this.hideModal()
        })
        .catch((error) => {
          console.error(error)
          this.$bvToast.toast('Error al solicitar excepción', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>
